import { useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";

import {
  DataGrid,
  TableLoader,
  TableFillHeightBody,
  shouldPinPrimaryColumnLeft,
} from "ui/data-grid";

import { createProgramTemplatesTableColumns } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table-columns";
import { ProgramTemplatesSelectionContext } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-selection-context";
import {
  useGetTouchpointVersionStatusesQuery,
  type TouchpointStatusDictionaryItem,
} from "hooks/api/dictionary";
import { useCurrentClient } from "hooks/api/use-clients";
import {
  AllSelectedData,
  DisplayedProgramTemplate,
} from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";

import type { CampaignTouchpointSummaryDTO } from "generated/schemas";
import type { PagedModelCampaignTouchpointSummaryDTO } from "generated/schemas";

const PRIMARY_COLUMN_IDS = ["checkbox", "name", "actions"];

// Module-scope so the columns keep their identity while the dictionary is still loading.
const NO_VERSION_STATUSES: TouchpointStatusDictionaryItem[] = [];

interface ProgramTemplatesTableProps {
  programTemplatesCampaignId: string;
  programTemplates: PagedModelCampaignTouchpointSummaryDTO["content"];
  isLoading: boolean;
  isFetching?: boolean;
  allSelectedData: AllSelectedData[];
  setAllSelectedData: (allSelectedData: AllSelectedData[]) => void;
  onRefetch?: () => void;
  hiddenColumnIds?: Set<string>;
}

const ProgramTemplatesTable = ({
  programTemplatesCampaignId,
  programTemplates,
  isLoading,
  isFetching = false,
  allSelectedData,
  setAllSelectedData,
  onRefetch,
  hiddenColumnIds,
}: ProgramTemplatesTableProps) => {
  const currentClient = useCurrentClient();
  const { data: touchpointVersionStatuses = NO_VERSION_STATUSES } =
    useGetTouchpointVersionStatusesQuery();

  // Local sorting state
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  // Map program templates to displayed format
  const displayedTemplates: DisplayedProgramTemplate[] = useMemo(
    () =>
      (programTemplates || []).map((template) => ({
        id: template.id!,
        name: template.name!,
        type: template.type || "",
        latestVersion: template.latestVersion
          ? {
              version: template.latestVersion.version ?? 0,
              status: template.latestVersion.status || "",
              id: template.latestVersion.id || "",
              applyClientTheme: template.latestVersion.applyClientTheme,
            }
          : null,
      })),
    [programTemplates],
  );

  const allColumns = useMemo(
    () =>
      createProgramTemplatesTableColumns({
        touchpointVersionStatuses,
        clientId: currentClient?.id || "",
        programTemplatesCampaignId,
        onRefetch,
      }),
    [touchpointVersionStatuses, currentClient?.id, programTemplatesCampaignId, onRefetch],
  );

  const selection = useMemo(
    () => ({ allSelectedData, setAllSelectedData, displayedTemplates }),
    [allSelectedData, setAllSelectedData, displayedTemplates],
  );

  const columns = useMemo(
    () =>
      hiddenColumnIds?.size
        ? allColumns.filter((col) => !hiddenColumnIds.has(col.id ?? ""))
        : allColumns,
    [allColumns, hiddenColumnIds],
  );

  const pinPrimaryColumnLeft = shouldPinPrimaryColumnLeft(columns, PRIMARY_COLUMN_IDS);

  return (
    <TableFillHeightBody>
      <TableLoader isLoading={isLoading} fullScreen />

      {isLoading ? null : programTemplates && programTemplates.length > 0 ? (
        <ProgramTemplatesSelectionContext value={selection}>
          <DataGrid
            columns={columns}
            data={(programTemplates || []) as CampaignTouchpointSummaryDTO[]}
            isLoading={isFetching}
            manualSorting={false}
            pagination={false}
            showingRecordsIsOn={false}
            viewByAmountSelectIsOn={false}
            totalRecords={programTemplates.length}
            pageSize={programTemplates.length}
            pagesAmount={1}
            pageIndex={0}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            pinPrimaryColumnLeft={pinPrimaryColumnLeft}
            fillHeight
          />
        </ProgramTemplatesSelectionContext>
      ) : (
        <p className="text-gray-500">There are no items in the list</p>
      )}
    </TableFillHeightBody>
  );
};

export default ProgramTemplatesTable;
